import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { loadIdentity, type Identity } from '../lib/identity';
import { toAppError, type AppError } from '../lib/errors';
import { useAuth } from './AuthProvider';
import { useCompany } from './CompanyProvider';

// Loads the backend application identity once the session is authenticated AND CompanyProvider
// has selected + installed an active company context (ADMINISTRATION-DESIGN §4, Slice 6C-1) —
// no company-scoped identity request may fire before selection. Provides the actorId
// (users.id) + company + roles used by the RPC layer, Company Switcher and guards.
type IdentityStatus = 'idle' | 'loading' | 'ready' | 'error' | 'anonymous';

interface IdentityState {
  readonly status: IdentityStatus;
  readonly identity: Identity | null;
  readonly error: AppError | null;
}

const IdentityContext = createContext<IdentityState | null>(null);

export function IdentityProvider({ children }: { children: ReactNode }): JSX.Element {
  const { status: authStatus, authUser } = useAuth();
  const { activeCompany } = useCompany();
  const [state, setState] = useState<IdentityState>({
    status: 'idle',
    identity: null,
    error: null,
  });

  const authUserId = authUser?.authUserId ?? null;
  const activeCompanyId = activeCompany?.id ?? null;

  useEffect(() => {
    if (authStatus === 'loading') {
      setState({ status: 'loading', identity: null, error: null });
      return;
    }
    if (authStatus === 'anonymous' || authUserId === null) {
      setState({ status: 'anonymous', identity: null, error: null });
      return;
    }
    if (activeCompanyId === null) {
      // Company not yet selected/installed — no company-scoped request may fire.
      setState({ status: 'loading', identity: null, error: null });
      return;
    }

    let active = true;
    setState({ status: 'loading', identity: null, error: null });
    void loadIdentity(authUserId)
      .then((identity) => {
        if (active) {
          setState({ status: 'ready', identity, error: null });
        }
      })
      .catch((err: unknown) => {
        if (active) {
          setState({ status: 'error', identity: null, error: toAppError(err) });
        }
      });
    return () => {
      active = false;
    };
  }, [authStatus, authUserId, activeCompanyId]);

  const value = useMemo<IdentityState>(() => state, [state]);

  return <IdentityContext.Provider value={value}>{children}</IdentityContext.Provider>;
}

export function useIdentity(): IdentityState {
  const ctx = useContext(IdentityContext);
  if (ctx === null) {
    throw new Error('useIdentity must be used within IdentityProvider');
  }
  return ctx;
}
